// client/src/components/plataforma/siteInstitucional/NavegacaoPage.js
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavItems } from '../../../hooks/useNavItems';
import { Plus, Edit, Trash2, Loader2, GripVertical, Compass, ExternalLink, FileText, MoreVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import { useHomePersonalizadaGate, HomePersonalizadaUpsellBanner } from './useHomePersonalizadaGate';

const NavegacaoPage = () => {
    const navigate = useNavigate();
    const { hasHomePersonalizada } = useHomePersonalizadaGate();
    const { navItems, setNavItems, catalog, loading, deleteNavItem, updateNavItemsOrder } = useNavItems();
    const [selectedItem, setSelectedItem] = useState(null);

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este item de navegação?')) {
            try {
                await deleteNavItem(id);
                setSelectedItem(null);
            } catch (err) {
                // hook já exibe o alerta de erro
            }
        }
    };

    const handleEdit = (item) => {
        setSelectedItem(null);
        navigate(`/site-institucional/navegacao/editar/${item._id}`);
    };

    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }

        const items = Array.from(navItems);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        const updatedItems = items.map((item, index) => ({ ...item, order: index }));

        setNavItems(updatedItems);
        updateNavItemsOrder(updatedItems).catch(() => {
            setNavItems(navItems);
        });
    };

    const catalogLabel = useMemo(() => {
        const map = {};
        catalog.forEach(c => { map[c.key] = c.label; });
        return map;
    }, [catalog]);

    const sortedItems = useMemo(() =>
        [...navItems].sort((a, b) => a.order - b.order),
        [navItems]
    );

    if (loading) {
        return (
            <div className="page-management-container flex justify-center items-center h-64">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }

    return (
        <div className="page-management-container">
            <PageHeader
                title="Navegação"
                icon={<Compass className="icon-title" />}
                navBack={{ to: '/site-institucional', text: 'Voltar' }}
            />

            {!hasHomePersonalizada && <HomePersonalizadaUpsellBanner />}

            <div className="content-panel mt-6">
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="nav-items-list" isDropDisabled={!hasHomePersonalizada}>
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {sortedItems.map((item, index) => (
                                    <Draggable key={item._id} draggableId={item._id} index={index} isDragDisabled={!hasHomePersonalizada}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col">
                                                <div {...provided.dragHandleProps} className="self-center cursor-grab text-gray-400 hover:text-gray-600 mb-2">
                                                    <GripVertical size={20} />
                                                </div>
                                                <div className="flex-grow">
                                                    <h4 className="font-semibold text-lg flex items-center gap-2">
                                                        {item.type === 'external' ? <ExternalLink size={16} /> : <FileText size={16} />}
                                                        {item.label}
                                                    </h4>
                                                    <p className="text-gray-600 text-sm mb-1">
                                                        {item.type === 'external' ? item.url : (catalogLabel[item.internalKey] || item.internalKey)}
                                                    </p>
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                        {item.type === 'external' ? 'Link Externo' : 'Página Interna'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center border-t pt-3 mt-auto">
                                                    <button onClick={() => setSelectedItem(item)} className="action-icon-button text-gray-600" title="Ações" disabled={!hasHomePersonalizada}><MoreVertical size={18} /></button>
                                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${item.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                        {item.active ? 'Ativo' : 'Inativo'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {sortedItems.length === 0 && !loading && <div className="col-span-full text-center py-12 text-gray-500">Nenhum item de navegação configurado — a barra de navegação está usando o padrão do sistema.</div>}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            <ActionsDrawer
                open={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                title={selectedItem?.label || 'Item de Navegação'}
                actions={selectedItem ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEdit(selectedItem),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedItem._id),
                        destructive: true,
                    },
                ] : []}
            />

            <FabButton onClick={() => navigate('/site-institucional/navegacao/nova')} title="Novo Item" icon={<Compass size={26} className="icon" />} disabled={!hasHomePersonalizada} />
        </div>
    );
};

export default NavegacaoPage;
